import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { commissionsApi, type CommissionClaimSummaryDto } from "@/api/commissions";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Card } from "@/design-system/components/ui";
import { ClaimStatusBadge } from "@/components/commissions/ClaimStatusBadge";
import { currency, percent, shortDate, truncate } from "@/components/commissions/formatters";
import { UnallocatedPoolNotice } from "@/components/commissions/UnallocatedPoolNotice";
import { useDateFormatter } from "@/utils/datetime";

type TabKey = "review" | "claimant" | "revenue" | "payment" | "escalation";

const TABS: Array<{ key: TabKey; label: string; description: string }> = [
  { key: "review", label: "Review", description: "Submitted and revised claims awaiting your read." },
  // A contested claim used to sit in none of these queues: the reviewer who
  // sent it back could find it again only by searching All Claims. An
  // escalated one is left to the Escalated tab, where it can be acted on.
  {
    key: "claimant",
    label: "With Claimant",
    description:
      "Contested claims waiting for their claimants. A claim returns to Review when its claimant revises or affirms it, and leaves if they withdraw it.",
  },
  { key: "revenue", label: "Revenue Confirmation", description: "Agreed claims waiting for cash receipt confirmation." },
  { key: "payment", label: "Payment", description: "Payable and partially-paid claims ready for disbursement." },
  // The Escalated tile counted these and led nowhere: a claim reaches review
  // round 3 and auto-escalates with no human involved, so escalations arrive on
  // their own and nothing listed them. `escalationQueue` has existed on the API
  // client the whole time with exactly one caller — a page no route reaches.
  {
    key: "escalation",
    label: "Escalated",
    description: "Claims awaiting a manager's decision. Open one to resolve the escalation.",
  },
];

export function AccountingReviewPage() {
  const [tab, setTab] = useState<TabKey>("review");
  const stats = useQuery({
    queryKey: commissionKeys.queueStats(),
    queryFn: () => commissionsApi.queueStats(),
  });

  const queue = useQuery({
    queryKey: commissionKeys.queue(tab),
    queryFn: () => {
      if (tab === "review") return commissionsApi.reviewQueue();
      if (tab === "claimant") return commissionsApi.awaitingClaimantQueue();
      if (tab === "revenue") return commissionsApi.revenueConfirmQueue();
      if (tab === "escalation") return commissionsApi.escalationQueue();
      return commissionsApi.paymentQueue();
    },
  });

  const depths = useMemo(
    () => ({
      review: stats.data?.reviewDepth ?? 0,
      claimant: stats.data?.awaitingClaimantDepth ?? 0,
      revenue: stats.data?.revenueConfirmDepth ?? 0,
      payment: stats.data?.paymentDepth ?? 0,
      escalation: stats.data?.escalationDepth ?? 0,
    }),
    [stats.data],
  );

  return (
    <div className="flex flex-col gap-space-5">
      <div>
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
          Commissions — Accounting
        </div>
        <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
          Review Queue
        </h1>
        <p className="text-body text-gray-mid mt-space-2 max-w-reading">
          Triage commission claims through review, revenue confirmation, and payment.
        </p>
      </div>

      <UnallocatedPoolNotice />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-space-3">
        <Tile label="Awaiting Review" value={depths.review} accent="gold" onSelect={() => setTab("review")} selected={tab === "review"} />
        <Tile label="Awaiting Claimant" value={depths.claimant} accent="neutral" onSelect={() => setTab("claimant")} selected={tab === "claimant"} />
        <Tile label="Awaiting Revenue" value={depths.revenue} accent="info" onSelect={() => setTab("revenue")} selected={tab === "revenue"} />
        <Tile label="Awaiting Payment" value={depths.payment} accent="success" onSelect={() => setTab("payment")} selected={tab === "payment"} />
        <Tile label="Escalated" value={depths.escalation} accent="warning" onSelect={() => setTab("escalation")} selected={tab === "escalation"} />
      </div>

      <div className="flex gap-space-2 border-b border-border-light">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={
              "px-space-3 h-10 font-display text-label font-semibold uppercase tracking-button " +
              "transition-colors duration-fast " +
              (tab === t.key
                ? "text-gold border-b-2 border-gold"
                : "text-gray-mid border-b-2 border-transparent hover:text-onyx")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <p className="text-small text-gray-mid -mt-space-3">
        {TABS.find((t) => t.key === tab)?.description}
      </p>

      <QueueTable rows={queue.data ?? []} loading={queue.isLoading} />
    </div>
  );
}

/** Each tile now selects its own queue. They were read-only counts, which is
 *  how "Escalated" came to be a number with nothing behind it. */
function Tile({
  label,
  value,
  accent,
  onSelect,
  selected,
}: {
  label: string;
  value: number;
  accent: "gold" | "info" | "success" | "warning" | "neutral";
  onSelect: () => void;
  selected: boolean;
}) {
  const color =
    accent === "gold"
      ? "text-gold"
      : accent === "info"
        ? "text-status-info"
        : accent === "success"
          ? "text-status-success"
          : accent === "warning"
            ? "text-status-warning"
            : "text-onyx";
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-md"
    >
      <Card className={selected ? "border-gold" : undefined}>
        <div className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
          {label}
        </div>
        <div className={`font-display font-bold text-h2 tabular-nums mt-space-2 ${color}`}>{value}</div>
      </Card>
    </button>
  );
}

function QueueTable({ rows, loading }: { rows: CommissionClaimSummaryDto[]; loading: boolean }) {
  const { tz } = useDateFormatter();
  if (loading) {
    return (
      <Card>
        <div className="font-display text-label uppercase tracking-label text-gray-mid">Loading queue…</div>
      </Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card>
        <div className="text-center py-space-4">
          <div className="font-display font-bold text-body text-onyx">Queue is empty.</div>
          <div className="text-small text-gray-mid mt-space-1">Nothing to action right now.</div>
        </div>
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-small">
          <thead className="bg-onyx">
            <tr>
              <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Claim #
              </th>
              <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Claimant
              </th>
              <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Pool
              </th>
              <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Description
              </th>
              <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Gross
              </th>
              <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                %
              </th>
              <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Amount
              </th>
              <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Status
              </th>
              <th className="px-space-3 py-space-3 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Round
              </th>
              <th className="px-space-3 py-space-3 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                Submitted
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-border-light hover:bg-gold-highlight/10 transition-colors duration-fast">
                <td className="px-space-3 py-space-3 font-mono">
                  <Link to={`/commissions/${c.id}`} className="text-gold-shadow hover:underline">
                    {c.claimNumber}
                  </Link>
                </td>
                <td className="px-space-3 py-space-3">{c.claimantDisplayName ?? c.claimantUserId}</td>
                <td className="px-space-3 py-space-3">
                  <span className="font-display text-label font-semibold tracking-label text-gray-mid">{c.poolCode}</span>{" "}
                  <span className="text-onyx">{c.poolName}</span>
                </td>
                <td className="px-space-3 py-space-3 text-gray-mid">{truncate(c.serviceDescription, 60)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">{currency(c.grossRevenue)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">{percent(c.commissionPercent)}</td>
                <td className="px-space-3 py-space-3 text-right tabular-nums font-semibold">
                  {currency(c.commissionAmount)}
                </td>
                <td className="px-space-3 py-space-3">
                  <ClaimStatusBadge status={c.status} />
                </td>
                <td className="px-space-3 py-space-3 text-right tabular-nums">
                  {c.reviewRound > 0 ? c.reviewRound : "—"}
                </td>
                {/* When it entered the queue, the queue's own order. A claim
                    submitted before the server recorded that falls back to
                    its creation, which is what this column always showed. */}
                <td className="px-space-3 py-space-3 text-gray-mid">{shortDate(c.submittedAt ?? c.createdAt, tz)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
